// Workflow tree — modern SaaS biotech pipeline panel.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { buildWorkflowTreeView, } from './langGraphPipelineTree';
export { PipelineBranchIcon, PipelineTreeIcon } from './langGraphPipelineIcons';
const ACCENT = '#3B5FFF';
const CHILD_ROW_H = 44;
const CONNECTOR_W = 24;
const EMPTY_SUBTASKS = [];
function connectorGeomEqual(a, b) {
    if (a.height !== b.height || a.branchYs.length !== b.branchYs.length)
        return false;
    return a.branchYs.every((y, i) => Math.abs(y - b.branchYs[i]) < 0.5);
}
function ChevronIcon({ open }) {
    return (React.createElement("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: `wf-chevron ${open ? 'wf-chevron-open' : ''}`, "aria-hidden": "true" },
        React.createElement("polyline", { points: "9 18 15 12 9 6" })));
}
function DoneIcon() {
    return (React.createElement("svg", { className: "wf-done-icon", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true" },
        React.createElement("circle", { cx: "12", cy: "12", r: "10", stroke: "#22c55e", strokeWidth: "1.5" }),
        React.createElement("polyline", { points: "8 12 11 15 16 9", stroke: "#22c55e", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round" })));
}
/** Cursor / selection pin — where the user currently is in the pipeline. */
function SelectionIcon() {
    return (React.createElement("svg", { className: "wf-selection-icon", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true" },
        React.createElement("circle", { cx: "12", cy: "12", r: "9", stroke: "#3B5FFF", strokeWidth: "1.75" }),
        React.createElement("circle", { cx: "12", cy: "12", r: "3.5", fill: "#3B5FFF" })));
}
function BranchConnectors({ branchYs, height, highlighted, }) {
    var _a;
    if (branchYs.length === 0 || height <= 0)
        return null;
    const lastY = (_a = branchYs[branchYs.length - 1]) !== null && _a !== void 0 ? _a : 0;
    // Spine from top of the children area down to the last bubble midpoint —
    // never hangs past a child row into empty space.
    const paths = [`M 0 0 V ${lastY}`];
    for (const y of branchYs) {
        // Curved elbow into the bubble (original pipeline style).
        paths.push(`M 0 ${y} H 8 a 6 6 0 0 1 6 6 l 6 -6`);
    }
    return (React.createElement("svg", { className: `wf-connectors ${highlighted ? 'wf-connectors-highlight' : ''}`, width: CONNECTOR_W, height: height, "aria-hidden": "true" }, paths.map((d, i) => (React.createElement("path", { key: i, d: d, fill: "none", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round" })))));
}
function StageBlock({ stage, expanded, onToggle, selectedStepId, onSelectStep, expandedSubtasks, onToggleSubtask, }) {
    const isMuted = stage.status === 'pending';
    const isActive = stage.status === 'active';
    const stageSelected = stage.subtasks.some((s) => s.isSelected);
    const [hovered, setHovered] = useState(false);
    const childrenRef = useRef(null);
    const childBtnRefs = useRef([]);
    const [connectorGeom, setConnectorGeom] = useState({
        branchYs: [],
        height: 0,
    });
    const visibleSubtasks = isMuted ? EMPTY_SUBTASKS : stage.subtasks;
    const visibleCount = visibleSubtasks.length;
    const measureConnectors = useCallback(() => {
        var _a;
        const root = childrenRef.current;
        const list = root === null || root === void 0 ? void 0 : root.querySelector('.wf-child-list');
        if (!root || !list || visibleCount === 0 || !expanded) {
            setConnectorGeom((prev) => prev.branchYs.length === 0 && prev.height === 0
                ? prev
                : { branchYs: [], height: 0 });
            return;
        }
        // Collapsed grid (0fr) reports ~0 height — don't measure phantom geometry.
        if (root.getBoundingClientRect().height < 2) {
            return;
        }
        const rootTop = root.getBoundingClientRect().top;
        const branchYs = visibleSubtasks.map((_, i) => {
            const btn = childBtnRefs.current[i];
            if (!btn)
                return i * CHILD_ROW_H + CHILD_ROW_H / 2;
            const r = btn.getBoundingClientRect();
            return r.top - rootTop + r.height / 2;
        });
        const lastY = (_a = branchYs[branchYs.length - 1]) !== null && _a !== void 0 ? _a : 0;
        // Height from the child list only — never from root.scrollHeight (includes the
        // SVG itself → ResizeObserver feedback loop → giant empty hole in the panel).
        const height = Math.max(list.offsetHeight, Math.ceil(lastY + CHILD_ROW_H / 2));
        const next = { branchYs, height };
        setConnectorGeom((prev) => (connectorGeomEqual(prev, next) ? prev : next));
    }, [visibleSubtasks, visibleCount, expanded]);
    useLayoutEffect(() => {
        measureConnectors();
    }, [measureConnectors, expanded, expandedSubtasks, selectedStepId, visibleCount]);
    useEffect(() => {
        const root = childrenRef.current;
        const list = root === null || root === void 0 ? void 0 : root.querySelector('.wf-child-list');
        if (!list || !expanded || typeof ResizeObserver === 'undefined')
            return;
        const ro = new ResizeObserver(() => measureConnectors());
        ro.observe(list);
        return () => ro.disconnect();
    }, [measureConnectors, expanded, visibleCount]);
    const handleParentClick = () => {
        if (isMuted)
            return;
        onToggle();
    };
    return (React.createElement("div", { className: `wf-stage wf-stage-${stage.status} ${expanded ? 'wf-stage-expanded' : ''}`, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) },
        React.createElement("button", { type: "button", className: `wf-parent ${isActive ? 'wf-parent-active' : ''} ${stageSelected ? 'wf-parent-selected' : ''} ${isMuted ? 'wf-parent-muted' : ''}`, onClick: handleParentClick, "aria-expanded": expanded && !isMuted, disabled: isMuted },
            React.createElement(ChevronIcon, { open: expanded }),
            React.createElement("span", { className: "wf-parent-label" }, stage.label),
            React.createElement("span", { className: "wf-parent-meta" },
                stageSelected && (React.createElement("span", { className: "wf-child-cursor-mark", title: "Selected step", "aria-label": "Selected" },
                    React.createElement(SelectionIcon, null))),
                stage.status === 'done' && (React.createElement("span", { className: "wf-child-done-mark", "aria-label": "Complete" },
                    React.createElement(DoneIcon, null))),
                stage.status === 'stale' && React.createElement("span", { title: "Stale after upstream change" }, "stale"),
                stage.status === 'locked' && React.createElement("span", { title: "Locked" }, "locked"),
                (stage.status === 'running' || stage.status === 'ready') && (React.createElement("span", { style: { fontSize: 10, opacity: 0.7 } }, stage.status)),
                React.createElement("span", { className: "wf-progress-pct" },
                    stage.progressPct,
                    "%"))),
        React.createElement("div", { className: `wf-children-wrap ${expanded ? 'wf-children-open' : ''}` },
            React.createElement("div", { className: "wf-children-inner" }, visibleSubtasks.length > 0 && (React.createElement("div", { className: "wf-children", ref: childrenRef },
                React.createElement(BranchConnectors, { branchYs: connectorGeom.branchYs, height: connectorGeom.height, highlighted: hovered || isActive }),
                React.createElement("div", { className: "wf-child-list" }, visibleSubtasks.map((sub, idx) => {
                    const selected = selectedStepId === sub.stepId || sub.isSelected;
                    const subExpanded = expandedSubtasks.has(sub.id);
                    const showDetails = sub.status === 'active' ||
                        sub.isSelected ||
                        (subExpanded && sub.status === 'done');
                    return (React.createElement("div", { key: sub.id, className: "wf-child-block" },
                        React.createElement("button", { type: "button", ref: (el) => {
                                childBtnRefs.current[idx] = el;
                            }, className: `wf-child wf-child-${sub.status} ${selected ? 'wf-child-selected' : ''} ${sub.isSelected ? 'wf-child-cursor' : ''}`, onClick: () => {
                                onSelectStep(sub.stepId);
                                onToggleSubtask(sub.id);
                            }, "aria-current": selected ? 'step' : undefined },
                            React.createElement("span", { className: "wf-child-dot", "aria-hidden": "true" }),
                            React.createElement("span", { className: "wf-child-label" }, sub.label),
                            React.createElement("span", { className: "wf-child-marks" },
                                sub.isSelected && (React.createElement("span", { className: "wf-child-cursor-mark", title: "Selected step", "aria-label": "Selected" },
                                    React.createElement(SelectionIcon, null))),
                                sub.status === 'done' && (React.createElement("span", { className: "wf-child-done-mark", "aria-label": "Complete" },
                                    React.createElement(DoneIcon, null))),
                                sub.status === 'active' && !sub.isSelected && (React.createElement("span", { className: "wf-child-active-pill" }, "In progress")))),
                        showDetails && sub.details.length > 0 && (React.createElement("ul", { className: "wf-details" }, sub.details.map((d) => (React.createElement("li", { key: `${sub.id}-${d.label}`, className: `wf-detail ${d.filled ? 'wf-detail-filled' : ''}` },
                            React.createElement("span", { className: "wf-detail-label" }, d.label),
                            React.createElement("span", { className: "wf-detail-value" }, d.value))))))));
                }))))))));
}
export default function PipelineGraphView({ data, selectedStepId, onSelectStep, }) {
    const stages = useMemo(() => { var _a; return buildWorkflowTreeView(data === null || data === void 0 ? void 0 : data.step_progress, (_a = data === null || data === void 0 ? void 0 : data.active_step) !== null && _a !== void 0 ? _a : data === null || data === void 0 ? void 0 : data.current_step, data); }, [data]);
    const [expandedStages, setExpandedStages] = useState(() => new Set());
    const [expandedSubtasks, setExpandedSubtasks] = useState(() => new Set());
    useEffect(() => {
        const next = new Set();
        stages.forEach((s) => {
            if (s.defaultExpanded)
                next.add(s.id);
        });
        setExpandedStages((prev) => {
            if (prev.size === next.size && [...next].every((id) => prev.has(id)))
                return prev;
            return next;
        });
    }, [stages]);
    useEffect(() => {
        var _a;
        const step = (_a = data === null || data === void 0 ? void 0 : data.active_step) !== null && _a !== void 0 ? _a : data === null || data === void 0 ? void 0 : data.current_step;
        if (!step)
            return;
        const activeStage = stages.find((s) => s.subtasks.some((sub) => sub.stepId === step));
        if (activeStage) {
            setExpandedStages((prev) => {
                if (prev.has(activeStage.id))
                    return prev;
                return new Set(prev).add(activeStage.id);
            });
            const activeSub = activeStage.subtasks.find((sub) => sub.isSelected || sub.status === 'active');
            if (activeSub) {
                setExpandedSubtasks((prev) => {
                    if (prev.has(activeSub.id))
                        return prev;
                    return new Set(prev).add(activeSub.id);
                });
            }
        }
    }, [data === null || data === void 0 ? void 0 : data.active_step, data === null || data === void 0 ? void 0 : data.current_step, stages]);
    const toggleStage = useCallback((stageId) => {
        setExpandedStages((prev) => {
            const next = new Set(prev);
            if (next.has(stageId))
                next.delete(stageId);
            else
                next.add(stageId);
            return next;
        });
    }, []);
    const toggleSubtask = useCallback((subId) => {
        setExpandedSubtasks((prev) => {
            const next = new Set(prev);
            if (next.has(subId))
                next.delete(subId);
            else
                next.add(subId);
            return next;
        });
    }, []);
    return (React.createElement("div", { className: "wf-tree", role: "tree", "aria-label": "Workflow pipeline" }, stages.map((stage) => (React.createElement(StageBlock, { key: stage.id, stage: stage, expanded: expandedStages.has(stage.id), onToggle: () => toggleStage(stage.id), selectedStepId: selectedStepId, onSelectStep: onSelectStep, expandedSubtasks: expandedSubtasks, onToggleSubtask: toggleSubtask })))));
}
export { ACCENT };
